import { useEffect, useRef } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Flex } from "@similarweb/ui-components/foundations";

import { TRIAL_BANNER_HEIGHT } from "app/components/React/TrialBanner/StyledTrialBanner";
import { AI_MEETING_AGENT_KB_ARTICLE_LINK } from "app/pages/sales-intelligence/constants/common";
import { AI_AGENT_MEETING_ROOT } from "app/pages/sales-intelligence/constants/routes";
import { AIAgentCaption } from "app/pages/sales-intelligence/modules/ai-agent/components/AIAgentCaption";
import { useCompanyName } from "app/pages/sales-intelligence/modules/ai-agent/hooks/useCompanyName";
import { useTrackCopyText } from "app/pages/sales-intelligence/modules/ai-agent/hooks/useTrackCopyText";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { AgentKnowledgeBase } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/AgentKnowledgeBase";
import { RedirectBriefModal } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/download-pdf/components/RedirectBriefModal/RedirectBriefModal";
import { AI_AGENT_PRICE_PER_RECORD } from "app/pages/sales-intelligence/modules/quota/constants";
import DataCreditsIndicatorContainer from "app/pages/sales-intelligence/modules/quota/containers/DataCreditsIndicatorContainer/DataCreditsIndicatorContainer";
import { useQuotaGuard } from "app/pages/sales-intelligence/modules/quota/hooks/useQuotaGuard";
import { AI_AGENT_PAGE_WIDTH } from "app/pages/sales-intelligence/pages/ai-agent/constants";
import { ActionsContainer } from "app/pages/sales-intelligence/pages/ai-agent/containers/ActionsContainer/ActionsContainer";
import AIAgentChat from "app/pages/sales-intelligence/pages/ai-agent/containers/AIAgentChat/AIAgentChat";
import FirstRespondForm from "app/pages/sales-intelligence/pages/ai-agent/containers/FirstRespondForm/FirstRespondForm";
import { useMeetingBriefGeneration } from "app/pages/sales-intelligence/pages/ai-agent/hooks/useMeetingBriefGeneration";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const AIAgentMeetingPageContent = () => {
    const containerRef = useRef(null);
    const { companyName } = useCompanyName();
    const { params } = useCurrentStateAndParams<{ id?: string }>();
    const { isBlocked } = useQuotaGuard(AI_AGENT_PRICE_PER_RECORD);
    const { brief, generate, retry, canRetry } = useMeetingBriefGeneration();

    useEffect(() => {
        if (isBlocked && !params.id) {
            void proNavigator.go(AI_AGENT_MEETING_ROOT);
        }
    }, [isBlocked, params.id]);

    const onCopyText = () => {
        AIAgentTrackingService.meeting.trackCopyText(companyName);
    };
    useTrackCopyText(onCopyText);

    const isTrialBannerVisible = isTrialOrAfterTrial();
    const topOffset = isTrialBannerVisible ? TRIAL_BANNER_HEIGHT : 0;

    return (
        <Flex
            flexDirection="column"
            alignItems="center"
            minHeight={isTrialBannerVisible ? `calc(100vh - ${TRIAL_BANNER_HEIGHT}px)` : "100vh"}
        >
            <ActionsContainer
                variant="chatPage"
                topOffset={topOffset}
            />
            <Flex
                flexDirection="column"
                width="100%"
                height="100%"
                overflowY="auto"
                margin="0 0 60px"
                ref={containerRef}
            >
                <AgentKnowledgeBase
                    position="sticky"
                    top={topOffset + 12}
                    helpLinkHref={AI_MEETING_AGENT_KB_ARTICLE_LINK}
                >
                    <DataCreditsIndicatorContainer />
                </AgentKnowledgeBase>
                <Flex
                    flexDirection="column"
                    alignItems="flex-start"
                    gap="52px"
                    width={`${AI_AGENT_PAGE_WIDTH}px`}
                    margin="0 auto"
                    padding="36px 0 12px"
                >
                    <FirstRespondForm
                        brief={brief}
                        onGenerate={generate}
                    />
                    <AIAgentChat
                        containerRef={containerRef}
                        onRetryBrief={retry}
                        isRetryBriefDisabled={!canRetry}
                    />
                </Flex>
            </Flex>
            <AIAgentCaption />
            <RedirectBriefModal />
        </Flex>
    );
};

export default AIAgentMeetingPageContent;
